import {
  Box,
  Button,
  Container,
  Stack,
  Table,
  Typography
} from "@mui/material";
import {
  Undo20Px,
  ProgressButton,
  useToast,
  ASTableV2RowEmpty,
  ScanningIndicator
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import useFlag from "~/config/flags";
import { useDevCheats } from "~/hooks/useDevCheats";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useNavbar } from "~/hooks/useNavbar";
import { runAsyncSafely } from "~/lib/helpers";
import {
  formatCycleCountProductMixpanelRowsForTableSelection,
  mixpanelTrack,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { setNoMoreBins } from "~/redux/actions";
import {
  clearSelectedCycleCounts,
  getCycleCountFrequencyByVid,
  setSelectedCycleCounts
} from "~/redux/actions/cycleCounts";
import {
  clearSelectedVariant,
  getVariantByVariantId
} from "~/redux/actions/inventory";
import { StoreState } from "~/redux/reducers";
import {
  useGetOverhaulIncompleteCycleCountsQuery,
  usePostStartCycleCountingMutation
} from "~/redux/warehouse/cycleCountsOverhaul.hooks";
import { useVariantsByIdQuery } from "~/redux/warehouse/variant.hooks";
import {
  FulfillmentCenterDto,
  IncompleteCycleCountResponseDto,
  VariantFrontendDto
} from "~/types/api";

import { CycleCountsSearch } from "./CycleCountsSearch";
import CycleCountsTableWrapper from "./CycleCountsTableWrapper";
import {
  setPage,
  setScannedBarcode,
  setScanState,
  setSearchedVariant
} from "./cycleCountsNew.slice";

interface CycleCountTaskDto
  extends IncompleteCycleCountResponseDto,
    VariantFrontendDto {}

const mapStateToProps = (
  state: StoreState
): {
  selectedAutostoreGridId: Guid | undefined;
  thisWorkstationId: Guid | undefined;
  usersFulfillmentCenter: FulfillmentCenterDto | null;
  initialFrequency: number | null;
  selectedVariant: VariantFrontendDto | null;
} => ({
  selectedAutostoreGridId: state.workstations.siteWorkstation?.autostoreGridId,
  usersFulfillmentCenter: state.store.usersFulfillmentCenter,
  thisWorkstationId: state.workstations.siteWorkstation?.id,
  initialFrequency: state.cycleCounts.cycleCountFrequency?.frequency,
  selectedVariant: state.inventory.selectedVariant
});

const connector = connect(mapStateToProps, {
  getCycleCountFrequencyByVid,
  getVariantByVariantId,
  clearSelectedVariant,
  setSelectedCycleCounts
});

type PropsFromRedux = ConnectedProps<typeof connector>;
export type CycleCountProps = PropsFromRedux;

export function CycleCountsV2(props: CycleCountProps) {
  const { selectedAutostoreGridId, initialFrequency, selectedVariant } = props;
  const trackedPageName = useMixpanelPageName();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { errorToast } = useToast();
  const page = useAppSelector((state) => state.cycleCountsNewSlice.page);
  const searchedVariant = useAppSelector(
    (state) => state.cycleCountsNewSlice.searchedVariant
  );
  const scanState = useAppSelector(
    (state) => state.cycleCountsNewSlice.scanState
  );
  const lastScannedBarcode = useAppSelector(
    (state) => state.cycleCountsNewSlice.scannedBarcode
  );
  const tempZoneFilters = useAppSelector(
    (state) => state.cycleCountsNewSlice.tempZoneFilters
  );

  const [initialFetchComplete, setInitialFetchComplete] =
    useState<boolean>(false);

  // table row data
  const [variantIds, setVariantIds] = useState<Guid[] | null>(null);
  const [selectedRows, setSelectedRows] = useState<
    {
      cycleCountId: string;
      variantId: string;
      binCount: number;
    }[]
  >([]);
  const [mergedData, setMergedData] = useState<CycleCountTaskDto[]>([]);
  const countingTaskBinCount = selectedRows?.length
    ? selectedRows.reduce((acc, row) => acc + row.binCount, 0)
    : 0;

  const navigate = useFcNavigate();
  const locationInfo = useLocation();

  const {
    cycleCountOverhaul: showCycleCountFrequencyCardDetails,
    tablesV2Enhanced
  } = useFlag();
  const hasCycleCountsTableV2EnhancedFlag =
    tablesV2Enhanced.includes("cycleCountTableV2");

  const { search } = locationInfo;
  // page vars
  const limit = 5;
  const offset = page >= 2 ? (page - 1) * limit : 0;

  const {
    data: cycleCountData,
    isLoading,
    refetch,
    isSuccess: isGetCycleCountsSuccess,
    isError: isGetCycleCountsError
  } = useGetOverhaulIncompleteCycleCountsQuery({
    offset,
    limit,
    gridId: selectedAutostoreGridId,
    AutoStoreBins: !!selectedAutostoreGridId,
    variantId: searchedVariant?.variantId,
    temperatureZones: tempZoneFilters?.length ? tempZoneFilters : undefined
  });

  const { data: cycleCountVariantData, isLoading: loadingVariantData } =
    useVariantsByIdQuery(
      variantIds?.length
        ? {
            variantIds: variantIds
          }
        : skipToken
    );
  const [startCycleCountingPost] = usePostStartCycleCountingMutation();

  const totalPageCount = cycleCountData?.totalCycleCounts
    ? Math.ceil(cycleCountData?.totalCycleCounts / limit)
    : 0;

  // maybe when this is called, if there is an acive-task group, we delete it and start a new one.
  const resetPage = useCallback(() => {
    setSelectedRows([]);
    dispatch(setPage(1));
    dispatch(setSearchedVariant(null));
    dispatch(setScannedBarcode(null));
    dispatch(setScanState("ready"));
  }, [dispatch]);

  useEffect(() => {
    dispatch(clearSelectedCycleCounts());
  }, [dispatch]);

  // fetch incomplete counting tasks
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!initialFetchComplete) {
      void refetch();
    }
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
    setInitialFetchComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFetchComplete]);

  // Fetch variants if we have counting tasks.
  useEffect(() => {
    if (cycleCountData?.cycleCounts?.length) {
      const allVariantIds = cycleCountData.cycleCounts.map(
        (task: IncompleteCycleCountResponseDto) => task.variantId
      );
      setVariantIds(allVariantIds);
    }
  }, [page, cycleCountData]);

  // pagination
  useEffect(() => {
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, page]);

  useEffect(() => {
    const getVariantRecordInformation = async () => {
      if (selectedRows?.length) {
        // get the last selected variantId to pass into the product card
        const lastSelectedRow = selectedRows[selectedRows.length - 1];
        await props.getVariantByVariantId(lastSelectedRow.variantId);
        await props.getCycleCountFrequencyByVid(lastSelectedRow.variantId);
      } else {
        props.clearSelectedVariant();
      }
    };
    void runAsyncSafely(getVariantRecordInformation);

    // merge variant & cc task data together
    const reducedVariantTaskData: CycleCountTaskDto[] = selectedRows.reduce(
      (acc: CycleCountTaskDto[], row) => {
        const alreadySelectedTask = mergedData.find(
          (data) => data.cycleCountId === row.cycleCountId
        );
        if (alreadySelectedTask) {
          acc.push(alreadySelectedTask);
          return acc;
        }

        const selectedTask: IncompleteCycleCountResponseDto | undefined =
          cycleCountData?.cycleCounts.find(
            (data) => data.cycleCountId === row.cycleCountId
          );
        const product = cycleCountVariantData?.find(
          (cycleCount) => cycleCount.variantId === row.variantId
        );

        if (selectedTask && product) {
          const mergedItem: CycleCountTaskDto = {
            ...selectedTask,
            ...product
          };
          acc.push(mergedItem);
        }

        return acc;
      },
      []
    );

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setMergedData(reducedVariantTaskData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows?.length]);

  const cycleCountsSearchbar = useMemo(() => <CycleCountsSearch />, []);
  const { setMenuItems } = useNavbar({
    centerComponent: !hasCycleCountsTableV2EnhancedFlag
      ? cycleCountsSearchbar
      : undefined,
    viewTitle: "nav.viewname.cycle counts"
  });
  useDevCheats({ showAutostoreStatus: true });
  useEffect(() => {
    setMenuItems([
      {
        textContent: t("refresh"),
        trackingLabel: "Refresh",
        actionCb: () => {
          resetPage();
          window.location.reload();
        }
      }
    ]);
  }, [t, setMenuItems, resetPage]);

  useEffect(() => {
    // reset page on unmount
    return resetPage();
  }, [resetPage]);

  return (
    <>
      <Stack padding={4} spacing={3} direction="row">
        {/* left side */}
        <Container maxWidth="sm" sx={{ height: 480 }}>
          {selectedVariant ? (
            <ProductCard.Root
              productName={selectedVariant.productName ?? ""}
              imageFileName={selectedVariant.imageFilename}
              tempZone={selectedVariant.temperatureZone}
            >
              <ProductCard.Sku sku={selectedVariant.sku} />
              <ProductCard.UpcList upcs={selectedVariant.allUpcs} />
              {showCycleCountFrequencyCardDetails && (
                <ProductCard.CycleCountFrequency
                  cycleCountFrequency={initialFrequency}
                />
              )}
            </ProductCard.Root>
          ) : (
            <ProductCard.Loading height={480} />
          )}
        </Container>
        {/* right side */}
        <Stack direction="column" width="100%">
          <>
            {!!searchedVariant && (
              <Button
                sx={{ alignSelf: "flex-end", mb: 2 }}
                onClick={() => resetPage()}
                startIcon={<Undo20Px fill="white" />}
                style={{ fontWeight: "normal" }}
              >
                {t("return to all cycle counts")}
              </Button>
            )}
            {cycleCountData?.cycleCounts?.length &&
            cycleCountData.totalCycleCounts > 0 ? (
              <CycleCountsTableWrapper
                cycleCountData={cycleCountData}
                cycleCountVariantData={cycleCountVariantData ?? []}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                isLoadingData={isLoading || loadingVariantData}
                totalPageCount={totalPageCount}
                page={page}
                hasCycleCountsTableV2EnhancedFlag={
                  hasCycleCountsTableV2EnhancedFlag
                }
              />
            ) : isGetCycleCountsSuccess || isGetCycleCountsError ? (
              <Table sx={{ mb: 5 }}>
                <ASTableV2RowEmpty>
                  {t("no cycle counts found")}
                </ASTableV2RowEmpty>
              </Table>
            ) : null}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end"
              }}
            >
              <ProgressButton
                variant="contained"
                color="primary"
                buttonSize="large"
                sx={{ paddingLeft: "20px", mt: 1 }}
                onClick={async () => {
                  if (selectedRows?.length) {
                    const variants = cycleCountVariantData ?? [];
                    mixpanelTrack({
                      trackedPageName,
                      type: "Button Click",
                      label: "Start counting",
                      eventProperties: {
                        "Products selected before Start counting":
                          selectedRows.length,
                        "Cycle Counts Product Data":
                          formatCycleCountProductMixpanelRowsForTableSelection(
                            selectedRows,
                            variants
                          )
                      }
                    });
                    try {
                      const cycleCountIds = mergedData.map(
                        (i) => i.cycleCountId
                      );
                      await startCycleCountingPost({
                        cycleCountIds,
                        gridId: selectedAutostoreGridId,
                        workstationId: props.thisWorkstationId
                      }).unwrap();
                      // reset variantId
                      props.clearSelectedVariant();
                      dispatch(setNoMoreBins(false));
                      if (mergedData?.length) {
                        props.setSelectedCycleCounts(mergedData);
                        navigate({
                          pathname: `/autostore-cyclecountv2/cycle-count-task`,
                          search
                        });
                      }
                    } catch (e) {
                      errorToast(getMessageFromRtkError(e));
                    }
                  }
                }}
                disabled={!selectedRows.length}
              >
                <Typography variant="body2" style={{ color: "#fff" }}>
                  {t("start counting")}
                  {!!selectedRows?.length && ` (${countingTaskBinCount})`}
                </Typography>
              </ProgressButton>
            </Box>
          </>
        </Stack>
      </Stack>
      <ScanningIndicator
        scanState={scanState}
        scannedBarcode={lastScannedBarcode}
        placeholderText="Scan Product"
      />
    </>
  );
}

export default connector(CycleCountsV2);
